import { NGA_SUBTYPES, RESOURCE_TYPE_FILTER } from './constants.js';

// Subtypes that are surfaced as their own resource type filters (e.g. Short-Form
// Writing, Guided Reading). They share a parent tool (Assessment) but should be
// treated as distinct tools when filtering, so a TOOL filter must not match them.
const SEPARATELY_FILTERED_SUBTYPES = new Set(Object.values(NGA_SUBTYPES));

function normalizeResourceTypeFilter (filter) {
  if (typeof filter === 'string') {
    return { type: RESOURCE_TYPE_FILTER.TOOL, value: filter };
  }
  if (filter && filter.type && filter.value) {
    return filter;
  }
  return null;
}

function isSameResourceTypeFilter (filterA, filterB) {
  const normalizedA = normalizeResourceTypeFilter(filterA);
  const normalizedB = normalizeResourceTypeFilter(filterB);

  if (!normalizedA || !normalizedB) return false;

  return normalizedA.type === normalizedB.type &&
    normalizedA.value === normalizedB.value;
}

function getAssignmentValueForFilterType (assignment, filterType) {
  switch (filterType) {
    case RESOURCE_TYPE_FILTER.TOOL:
      return assignment.tool;
    case RESOURCE_TYPE_FILTER.LABEL:
      return assignment.labelKey || assignment.label_key;
    case RESOURCE_TYPE_FILTER.SUBTYPE:
      return assignment.subtype;
    default:
      return undefined;
  }
}

function matchesResourceTypeFilterEntry (assignment, filter) {
  const normalized = normalizeResourceTypeFilter(filter);
  if (!normalized) return false;

  const { type, value } = normalized;

  // A TOOL filter (e.g. "Assessment") must exclude assignments whose subtype is
  // filtered separately, so Short-Form Writing and Guided Reading behave as if
  // they were their own tools rather than part of the parent tool.
  if (type === RESOURCE_TYPE_FILTER.TOOL &&
    SEPARATELY_FILTERED_SUBTYPES.has(assignment.subtype)) {
    return false;
  }

  return getAssignmentValueForFilterType(assignment, type) === value;
}

function matchesResourceTypeFilter (assignment, filterTypes) {
  if (!filterTypes || filterTypes.length === 0) return true;

  return filterTypes.some((filter) =>
    matchesResourceTypeFilterEntry(assignment, filter)
  );
}

function isResourceTypeFilterActive (activeFilters, filter) {
  return activeFilters.some((activeFilter) =>
    isSameResourceTypeFilter(activeFilter, filter)
  );
}

function isResourceTypeFilterOptionActive (activeFilters, filters) {
  return filters.some((filter) => isResourceTypeFilterActive(activeFilters, filter));
}

function addResourceTypeFilters (activeFilters, filters) {
  const filtersToAdd = filters.filter(
    (filter) => !isResourceTypeFilterActive(activeFilters, filter)
  );

  return [...activeFilters, ...filtersToAdd];
}

function removeResourceTypeFilters (activeFilters, filters) {
  return activeFilters.filter(
    (activeFilter) => !filters.some((filter) =>
      isSameResourceTypeFilter(activeFilter, filter)
    )
  );
}

export {
  normalizeResourceTypeFilter,
  isSameResourceTypeFilter,
  matchesResourceTypeFilterEntry,
  matchesResourceTypeFilter,
  isResourceTypeFilterActive,
  isResourceTypeFilterOptionActive,
  addResourceTypeFilters,
  removeResourceTypeFilters
};
